// Locale-aware <Money> for customer-web.
//
// `@novemai/components`'s Money takes `locale` as a PROP defaulting to the SDK
// source locale, deliberately: the design-system package carries no i18n
// framework dependency (no i18next in its package.json) and must stay
// framework-agnostic. Its own docstring tells callers to pass the app's
// resolved locale.
//
// Callers did not. 40 of 41 customer-web call sites omitted it, so every
// salary, benefit cost, asset value and pay-run total rendered in en-US on a
// tr-TR screen — "TRY 151,200.00" where Turkish writes "151.200,00 ₺"
// (thousands dot, decimal comma). Fixing that at 40 call sites would leave the
// 41st — and every future one — free to forget again.
//
// So the injection lives HERE, once: customer-web imports Money from this
// module instead of the package. The design system stays agnostic, the app
// supplies its own resolved locale, and a new call site gets it for free.
// An explicit `locale` prop still wins, for the rare surface that must pin a
// currency's home formatting.

import { useTranslation } from "react-i18next";

import { Money as BaseMoney, type MoneyProps } from "@novemai/components";
import { isSupportedLocale } from "@novemai/i18n-sdk";

export type { MoneyProps };

export function Money(props: MoneyProps): React.ReactElement {
  const { i18n } = useTranslation();
  const { locale, ...rest } = props;
  // i18n.language is a bare string and may be a language-only tag or something
  // unsupported mid-switch; narrow it rather than casting. Falling through to
  // BaseMoney's own default keeps the previous behaviour for that case.
  const resolved = locale ?? (isSupportedLocale(i18n.language) ? i18n.language : undefined);
  return resolved === undefined ? (
    <BaseMoney {...rest} />
  ) : (
    <BaseMoney locale={resolved} {...rest} />
  );
}
